import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:60401/api';

interface NewsSource {
  id: number;
  name: string;
  url: string;
  type: string;
  language: string;
  default_category_id: number | null;
  default_category_name: string | null;
  fetch_interval_min: number;
  last_fetched_at: string | null;
  last_status: string | null;
  auto_publish: boolean;
  is_active: boolean;
}

interface Category {
  id: number;
  slug: string;
  name_de: string;
}

interface EditForm {
  name: string;
  url: string;
  type: string;
  language: string;
  default_category_id: number | null;
  fetch_interval_min: number;
  auto_publish: boolean;
  is_active: boolean;
}

const EMPTY_FORM: EditForm = { name: '', url: '', type: 'rss', language: 'de', default_category_id: null, fetch_interval_min: 30, auto_publish: false, is_active: true };

const COL: React.CSSProperties = { padding: '10px 12px', fontSize: '0.75rem', whiteSpace: 'nowrap', textAlign: 'left' };
const HDR: React.CSSProperties = { ...COL, color: '#4A5068', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #1E2330' };
const INPUT: React.CSSProperties = { width: '100%', padding: '8px 12px', backgroundColor: '#1E2330', border: '1px solid #2A3040', borderRadius: '6px', color: '#E8EAED', fontSize: '0.8rem', outline: 'none' };
const LABEL: React.CSSProperties = { display: 'block', fontSize: '0.65rem', fontWeight: 600, color: '#7B8399', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' };

export default function NewsSourcesPage() {
  const [rows, setRows] = useState<NewsSource[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<EditForm>(EMPTY_FORM);
  const [triggering, setTriggering] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/admin/news-sources`).then(r => r.json()),
      fetch(`${API_BASE}/admin/article-categories`).then(r => r.json()),
    ]).then(([s, c]) => { setRows(s); setCats(c); }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startEdit = (s: NewsSource) => {
    setCreating(false); setEditId(s.id);
    setForm({
      name: s.name, url: s.url, type: s.type, language: s.language,
      default_category_id: s.default_category_id,
      fetch_interval_min: s.fetch_interval_min,
      auto_publish: s.auto_publish, is_active: s.is_active,
    });
  };
  const startCreate = () => { setEditId(null); setCreating(true); setForm(EMPTY_FORM); };
  const cancel = () => { setEditId(null); setCreating(false); };

  const save = async () => {
    const payload = { ...form };
    if (creating) {
      await fetch(`${API_BASE}/admin/news-sources`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    } else if (editId) {
      await fetch(`${API_BASE}/admin/news-sources/${editId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    }
    cancel(); load();
  };
  const remove = async (id: number) => {
    if (!window.confirm('Quelle wirklich löschen?')) return;
    await fetch(`${API_BASE}/admin/news-sources/${id}`, { method: 'DELETE' });
    load();
  };
  const toggleActive = async (s: NewsSource) => {
    await fetch(`${API_BASE}/admin/news-sources/${s.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_active: !s.is_active }) });
    load();
  };
  const triggerFetch = async (s: NewsSource) => {
    const key = window.prompt('Interner Testlauf — X-Internal-Key eingeben:');
    if (!key) return;
    setTriggering(s.id);
    try {
      const r = await fetch(`${API_BASE}/internal/news/collect-source`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Internal-Key': key },
        body: JSON.stringify({ source_id: s.id }),
      });
      const body = await r.json();
      alert(r.ok ? `OK — neu: ${body.new ?? 0}, übersprungen: ${body.skipped ?? 0}` : `Fehler: ${body.error || r.status}`);
    } finally {
      setTriggering(null);
      load();
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: '#E8EAED', fontSize: '1.3rem' }}>News Quellen</h2>
        <button onClick={startCreate} style={{ padding: '8px 16px', background: '#4A6CF7', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>+ Neue Quelle</button>
      </div>

      {loading ? <div style={{ color: '#7B8399' }}>Laden…</div> : (
        <div style={{ background: '#15181E', border: '1px solid #1E2330', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={HDR}>Name</th>
                <th style={HDR}>Sprache</th>
                <th style={HDR}>Kategorie</th>
                <th style={HDR}>Intervall</th>
                <th style={HDR}>Auto-Publish</th>
                <th style={HDR}>Letzter Lauf</th>
                <th style={HDR}>Status</th>
                <th style={HDR}>Aktiv</th>
                <th style={HDR}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <React.Fragment key={s.id}>
                  <tr onClick={() => startEdit(s)} style={{ borderTop: '1px solid #1E2330', cursor: 'pointer', opacity: s.is_active ? 1 : 0.55 }}>
                    <td style={{ ...COL, color: '#E8EAED', fontWeight: 600 }}>{s.name}</td>
                    <td style={{ ...COL, color: '#7B8399', textTransform: 'uppercase' }}>{s.language}</td>
                    <td style={{ ...COL, color: '#7B8399' }}>{s.default_category_name || '—'}</td>
                    <td style={{ ...COL, color: '#7B8399' }}>{s.fetch_interval_min} min</td>
                    <td style={COL}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.65rem', background: s.auto_publish ? '#1A3A2E' : '#2A2F3A', color: s.auto_publish ? '#A8E6B8' : '#7B8399' }}>{s.auto_publish ? 'JA' : 'nein'}</span></td>
                    <td style={{ ...COL, color: '#7B8399', fontSize: '0.7rem' }}>{s.last_fetched_at ? new Date(s.last_fetched_at).toLocaleString('de-DE') : '—'}</td>
                    <td style={COL}>{s.last_status ? <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.65rem', background: s.last_status === 'ok' ? '#1A3A2E' : '#3A1A1A', color: s.last_status === 'ok' ? '#A8E6B8' : '#E6A8A8' }}>{s.last_status}</span> : <span style={{ color: '#4A5068' }}>—</span>}</td>
                    <td style={COL}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.65rem', background: s.is_active ? '#1A3A2E' : '#3A1A1A', color: s.is_active ? '#A8E6B8' : '#E6A8A8' }}>{s.is_active ? 'JA' : 'NEIN'}</span></td>
                    <td style={COL} onClick={e => e.stopPropagation()}>
                      <button onClick={() => triggerFetch(s)} disabled={triggering === s.id} title="Jetzt testen" style={{ padding: '4px 8px', marginRight: 4, background: 'transparent', border: '1px solid #2A3040', color: '#E8EAED', borderRadius: 4, cursor: 'pointer', fontSize: '0.7rem' }}>{triggering === s.id ? '…' : '▶'}</button>
                      <button onClick={() => toggleActive(s)} title={s.is_active ? 'Deaktivieren' : 'Aktivieren'} style={{ padding: '4px 8px', marginRight: 4, background: 'transparent', border: '1px solid #2A3040', color: '#E8EAED', borderRadius: 4, cursor: 'pointer', fontSize: '0.7rem' }}>{s.is_active ? '⏸' : '▶'}</button>
                      <button onClick={() => remove(s.id)} title="Löschen" style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #2A3040', color: '#E6A8A8', borderRadius: 4, cursor: 'pointer', fontSize: '0.7rem' }}>✕</button>
                    </td>
                  </tr>
                  {editId === s.id && (
                    <tr><td colSpan={9} style={{ padding: 0 }}>{renderForm(form, setForm, cats, save, cancel, false)}</td></tr>
                  )}
                </React.Fragment>
              ))}
              {creating && (
                <tr><td colSpan={9} style={{ padding: 0 }}>{renderForm(form, setForm, cats, save, cancel, true)}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function renderForm(form: EditForm, setForm: (f: EditForm) => void, cats: Category[], save: () => void, cancel: () => void, creating: boolean) {
  const set = (k: keyof EditForm, v: any) => setForm({ ...form, [k]: v });
  return (
    <div style={{ padding: 20, background: '#0F1116', borderTop: '1px solid #1E2330' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div><label style={LABEL}>Name *</label><input style={INPUT} value={form.name} onChange={e => set('name', e.target.value)} /></div>
        <div><label style={LABEL}>RSS/Atom/API-URL *</label><input style={INPUT} value={form.url} onChange={e => set('url', e.target.value)} /></div>
        <div><label style={LABEL}>Typ</label><select style={INPUT} value={form.type} onChange={e => set('type', e.target.value)}><option value="rss">RSS</option><option value="atom">Atom</option><option value="api">JSON API</option></select></div>
        <div><label style={LABEL}>Sprache *</label><select style={INPUT} value={form.language} onChange={e => set('language', e.target.value)}><option value="de">Deutsch</option><option value="en">Englisch</option></select></div>
        <div><label style={LABEL}>Standard-Kategorie</label><select style={INPUT} value={form.default_category_id ?? ''} onChange={e => set('default_category_id', e.target.value ? parseInt(e.target.value) : null)}><option value="">— wählen —</option>{cats.map(c => <option key={c.id} value={c.id}>{c.name_de}</option>)}</select></div>
        <div><label style={LABEL}>Abruf-Intervall (Minuten)</label><input type="number" style={INPUT} value={form.fetch_interval_min} onChange={e => set('fetch_interval_min', parseInt(e.target.value) || 30)} /></div>
        <div><label style={{ ...LABEL, display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.auto_publish} onChange={e => set('auto_publish', e.target.checked)} />Automatisch veröffentlichen</label></div>
        <div><label style={{ ...LABEL, display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />Aktiv</label></div>
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button onClick={save} style={{ padding: '8px 16px', background: '#4A6CF7', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem' }}>{creating ? 'Anlegen' : 'Speichern'}</button>
        <button onClick={cancel} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #2A3040', color: '#E8EAED', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem' }}>Abbrechen</button>
      </div>
    </div>
  );
}
